import { Button } from 'components/Button'
import { cn } from 'lib/utils'
import { motion } from 'framer-motion'
import { ChevronDown, Send } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ScheduleButton } from './ScheduleButton'

const EXPAND_OPEN_S = 0.28
const EXPAND_CLOSE_S = 0.22

const EASE_OUT = [0.22, 1, 0.36, 1] as const
const EASE_IN = [0.55, 0, 1, 0.45] as const

const panelOpenTransition = { duration: EXPAND_OPEN_S, ease: EASE_OUT }
const panelCloseTransition = { duration: EXPAND_CLOSE_S, ease: EASE_IN }

const contentOpenTransition = {
  duration: EXPAND_OPEN_S * 0.75,
  ease: EASE_OUT,
  delay: 0.05,
}

const contentCloseTransition = {
  duration: EXPAND_CLOSE_S * 0.6,
  ease: EASE_IN,
}

const SPLIT_COLS = 'grid-cols-[minmax(0,1fr)_30px]'
const CARET_W = 30
const CARET_COL = 'w-[30px] min-w-[30px] max-w-[30px]'
/** Acid `p-px` frame on each long edge of the shell. */
const ACID_FRAME_INSET_PX = 2

const ACID_HAIRLINE = 'bg-[rgba(10,12,0,0.22)]'
const CARET_DIVIDER = 'border-l border-l-[rgba(10,12,0,0.22)]'
/** Vertical split on dark post-now chrome — acid-tinted so it reads with the frame. */
const DARK_HAIRLINE = 'border-l border-l-[color-mix(in_srgb,var(--acid)_50%,transparent)]'

function splitRowMetrics(size: 'sm' | 't3-md') {
  return size === 't3-md'
    ? 'h-10 gap-2 pl-3.5 pr-4 text-[14px] font-semibold tracking-[-0.005em]'
    : 'h-8 gap-1.5 pl-2.5 pr-3 text-[13px] font-semibold tracking-[-0.005em]'
}

/** Row height (h-8/h-10) + hairline + p-px acid frame. */
function panelHeight(size: 'sm' | 't3-md') {
  return (size === 't3-md' ? 41 : 33) + ACID_FRAME_INSET_PX
}

/**
 * Inline accordion for the "Post now" secondary action.
 *
 * The caret half of a split button toggles a row that grows from the same
 * chrome — no portaled dropdown. The primary CTA stays fixed; only the panel
 * opens/closes in an absolutely-positioned layer above or below it.
 */
export function PostNowMenu({
  primary,
  onPostNow,
  variant = 't3-primary',
  size = 'sm',
  disabled = false,
  side = 'bottom',
  label = 'Post now',
}: {
  primary: ReactNode
  onPostNow: () => void
  variant?: 't3-primary' | 't3-tertiary'
  size?: 'sm' | 't3-md'
  disabled?: boolean
  side?: 'top' | 'bottom'
  label?: string
}) {
  const [open, setOpen] = useState(false)
  /** Keeps acid frame/chrome through the close animation after `open` is false. */
  const [frameVisible, setFrameVisible] = useState(false)
  const [lockedWidth, setLockedWidth] = useState<number | null>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const primaryRowRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const rowMetrics = splitRowMetrics(size)
  const frameActive = open || frameVisible

  const close = useCallback(() => setOpen(false), [])

  const syncLockedWidth = useCallback(() => {
    if (open) return
    const row = primaryRowRef.current
    if (!row) return
    const btn = row.querySelector<HTMLElement>('[data-slot=button]')
    const rowContent = btn ? Math.ceil(btn.scrollWidth) + CARET_W : Math.ceil(row.scrollWidth)
    const next = rowContent + ACID_FRAME_INSET_PX
    setLockedWidth(prev => (prev === next ? prev : next))
  }, [open])

  useLayoutEffect(() => {
    const row = primaryRowRef.current
    if (!row) return

    let raf = 0
    const measure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => syncLockedWidth())
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(row)
    const btn = row.querySelector('[data-slot=button]')
    if (btn) ro.observe(btn)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [syncLockedWidth, size])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (e: PointerEvent) => {
      if (!shellRef.current?.contains(e.target as Node)) close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const toggleOpen = () => {
    setOpen(v => {
      if (!v) setFrameVisible(true)
      return !v
    })
  }

  const handlePostNow = () => {
    close()
    onPostNow()
  }

  const caretGutter = (
    <div aria-hidden className={cn(CARET_COL, DARK_HAIRLINE, 'self-stretch bg-bg-3')} />
  )

  const rowHairline = <div aria-hidden className={cn('col-span-2 h-px shrink-0', ACID_HAIRLINE)} />

  const postNowButton = (
    <button
      type="button"
      role="menuitem"
      tabIndex={open ? 0 : -1}
      disabled={disabled}
      onClick={handlePostNow}
      className={cn(
        'col-start-1 flex min-w-0 items-center justify-start transition-colors',
        rowMetrics,
        'bg-bg-3 text-acid hover:bg-bg-2 disabled:pointer-events-none disabled:opacity-40'
      )}
    >
      <Send size={14} className="shrink-0" />
      {label}
    </button>
  )

  const panelInner = (
    <div className={cn('grid w-full min-w-0', SPLIT_COLS)}>
      {side === 'bottom' && rowHairline}
      {postNowButton}
      {caretGutter}
      {side === 'top' && rowHairline}
    </div>
  )

  const expandedPanel = (
    <motion.div
      id={panelId}
      role="menu"
      aria-hidden={!open}
      className={cn(
        'absolute left-0 z-10 w-full overflow-hidden',
        side === 'top' ? 'bottom-full' : 'top-full',
        frameActive && 'bg-[var(--acid)] p-px',
        frameActive && side === 'top' && 'rounded-t-[6px]',
        frameActive && side === 'bottom' && 'rounded-b-[6px]',
        !open && 'pointer-events-none'
      )}
      initial={false}
      animate={{ maxHeight: open ? panelHeight(size) : 0 }}
      transition={open ? panelOpenTransition : panelCloseTransition}
      onAnimationComplete={() => {
        if (!open) {
          setFrameVisible(false)
          syncLockedWidth()
        }
      }}
    >
      <div
        className={cn(
          'min-h-0 min-w-0 overflow-hidden bg-bg-3',
          side === 'top' ? 'rounded-t-[5px]' : 'rounded-b-[5px]'
        )}
      >
        <motion.div
          className="min-w-0"
          initial={false}
          animate={{ opacity: open ? 1 : 0 }}
          transition={open ? contentOpenTransition : contentCloseTransition}
        >
          {panelInner}
        </motion.div>
      </div>
    </motion.div>
  )

  const caret = (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={disabled}
      aria-label="More publish options"
      aria-haspopup="menu"
      aria-expanded={open}
      {...(open ? { 'aria-controls': panelId } : {})}
      onClick={toggleOpen}
      className={cn(
        CARET_COL,
        'col-start-2',
        'group shrink-0 self-stretch rounded-l-none',
        CARET_DIVIDER,
        '!gap-0 !p-0 !px-0 has-[>svg]:!p-0 has-[>svg]:!pl-0',
        'inline-flex items-center justify-center',
        '!translate-y-0 hover:!translate-y-0 active:!translate-y-0',
        '!shadow-none',
        frameActive && side === 'bottom' && 'rounded-br-none',
        frameActive && side === 'top' && 'rounded-tr-none'
      )}
    >
      <motion.span
        className="inline-flex shrink-0"
        initial={false}
        animate={{ rotate: open ? 180 : 0 }}
        transition={open ? panelOpenTransition : panelCloseTransition}
      >
        <ChevronDown size={14} className="shrink-0" />
      </motion.span>
    </Button>
  )

  const primaryCell = (
    <div
      className={cn(
        'col-start-1 min-w-0',
        '[&_[data-slot=button]]:!translate-y-0',
        '[&_[data-slot=button]]:hover:!translate-y-0',
        '[&_[data-slot=button]]:active:!translate-y-0',
        '[&_[data-slot=button]]:!shadow-none',
        frameActive && side === 'bottom' && '[&_[data-slot=button]:not(.group)]:rounded-b-none',
        frameActive && side === 'top' && '[&_[data-slot=button]:not(.group)]:rounded-t-none',
        '[&>div]:flex [&>div]:w-full [&>div]:min-w-0',
        '[&_[data-slot=button]:not(.group)]:w-full',
        '[&_[data-slot=button]:not(.group)]:min-w-0',
        '[&_[data-slot=button]:not(.group)]:justify-start',
        '[&_[data-slot=button]:not(.group)]:whitespace-nowrap'
      )}
    >
      {primary}
    </div>
  )

  return (
    <div
      ref={shellRef}
      style={
        lockedWidth != null
          ? {
              width: lockedWidth,
              minWidth: lockedWidth,
              maxWidth: lockedWidth,
              boxSizing: 'border-box',
            }
          : undefined
      }
      className={cn(
        'relative box-border inline-flex rounded-[6px] bg-[var(--acid)] p-px',
        frameActive && side === 'top' && 'rounded-t-none rounded-b-[6px]',
        frameActive && side === 'bottom' && 'rounded-t-[6px] rounded-b-none'
      )}
    >
      {expandedPanel}
      <div
        ref={primaryRowRef}
        className={cn(
          'relative z-[1] grid w-full min-w-0 items-stretch overflow-hidden bg-acid',
          SPLIT_COLS,
          !frameActive && 'rounded-[5px]',
          frameActive && side === 'top' && 'rounded-t-none rounded-b-[5px]',
          frameActive && side === 'bottom' && 'rounded-t-[5px] rounded-b-none'
        )}
      >
        {primaryCell}
        {caret}
      </div>
    </div>
  )
}

interface ScheduleSplitButtonProps {
  scheduledAt: string | null | undefined
  onSchedule: () => void
  onPostNow: () => void
  postContext: boolean
  justScheduled?: boolean
  disabled?: boolean
  livePermalink?: string
  needsApproval?: boolean
}

export function ScheduleSplitButton({
  scheduledAt,
  onSchedule,
  onPostNow,
  postContext,
  justScheduled = false,
  disabled = false,
  livePermalink,
  needsApproval = false,
}: ScheduleSplitButtonProps) {
  const showCaret = postContext && !disabled && !needsApproval
  const scheduleButtonProps = {
    scheduledAt,
    onClick: onSchedule,
    postContext,
    justScheduled,
    disabled,
    livePermalink,
    needsApproval,
  }

  if (!showCaret) {
    return <ScheduleButton {...scheduleButtonProps} />
  }

  return (
    <PostNowMenu
      side="bottom"
      onPostNow={onPostNow}
      variant="t3-primary"
      size="sm"
      disabled={disabled}
      primary={
        <ScheduleButton {...scheduleButtonProps} className="w-full justify-start rounded-r-none" />
      }
    />
  )
}
